import { useState, useEffect } from 'react';
import LiveRegion from '../components/ui/LiveRegion';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useLocation, useNavigate } from 'react-router-dom';

export function Login() {
  const { signInWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showConfirmationBanner, setShowConfirmationBanner] = useState(location.state?.needsConfirmation || false);
  const [authStep, setAuthStep] = useState<'enterEmail' | 'enterPassword'>('enterEmail');

  useEffect(() => {
    if (window.location.pathname === '/reset-password') {
      navigate('/reset-password', { replace: true });
    }

    // Store the redirect destination from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const redirectTo = searchParams.get('redirectTo');
    if (redirectTo) {
      sessionStorage.setItem('authRedirectTo', redirectTo);
      // Mobile fallback: Also store in localStorage as backup
      localStorage.setItem('authRedirectTo', redirectTo);
    }
  }, [navigate, location.search]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setEmailError("Please enter your email address");
      return;
    }

    try {
      setIsResetLoading(true);
      setEmailError(null);
      setResetMessage(null);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      
      setResetMessage("Password reset instructions sent to your email");
    } catch (err: unknown) {
      console.error('Password reset error:', err);
      if (err instanceof Error) {
        if (err.message.includes("rate limit exceeded")) {
          setEmailError("Too many reset attempts. Please try again in a few minutes.");
        } else if (err.message.includes("sending recovery email")) {
          // For development, show a message that would let us proceed
          setResetMessage("For development: If this were production, a reset link would be sent to " + email);
        } else {
          setEmailError(err.message || 'Failed to send password reset email');
        }
      } else {
        setEmailError('An unexpected error occurred');
      }
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleToggleMode = () => {
    setEmailError(null);
    setPassword('');
    setConfirmPassword('');
    setShowPasswords(false);
    setIsSignUp(!isSignUp);
    setAuthStep('enterEmail');
    setEmail('');
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setGoogleError(null);
    try {
      await signInWithGoogle();
      // Redirect will be handled by Supabase OAuth
    } catch (err) {
      console.error('Error signing in:', err);
      setGoogleError('Failed to sign in with Google. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  const handleContinueToPasswordStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!email) {
      setEmailError("Email address is required");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setEmailError(null);
    setAuthStep('enterPassword');
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsEmailLoading(true);
    setEmailError(null);

    if (authStep === 'enterPassword' && !password && !isSignUp) {
      setEmailError("Password is required");
      setIsEmailLoading(false);
      return;
    }
    if (authStep === 'enterPassword' && isSignUp && !password) {
      setEmailError("Password is required for sign up");
      setIsEmailLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setEmailError("Passwords don't match");
          setIsEmailLoading(false);
          return;
        }
        
        // Preserve redirect destination for email confirmation flow
        const redirectTo = sessionStorage.getItem('authRedirectTo') || localStorage.getItem('authRedirectTo');
        const emailRedirectUrl = redirectTo 
          ? `${window.location.origin}/auth/callback?redirectTo=${encodeURIComponent(redirectTo)}`
          : `${window.location.origin}/auth/callback`;
        
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: emailRedirectUrl
          }
        });
        if (error) {
          if (error.message.includes("already registered")) {
            throw new Error("An account with this email already exists. Please sign in instead.");
          }
          throw error;
        }
        
        setShowConfirmationBanner(true);
        setPassword('');
        setConfirmPassword('');
        setShowPasswords(false);
        setIsSignUp(false);
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;

        // Honor a pending redirect (e.g. co-parent /accept-invite?token=...) ourselves.
        // Unlike Google/email-confirmation, password sign-in completes in place and never
        // routes through /auth/callback, so without this the destination is dropped and
        // PublicRoute sends the user to /dashboard. Read sessionStorage only (tab-scoped,
        // always survives an in-place sign-in) to avoid acting on a stale localStorage copy.
        const redirectTo = sessionStorage.getItem('authRedirectTo');
        if (redirectTo) {
          sessionStorage.removeItem('authRedirectTo');
          localStorage.removeItem('authRedirectTo');
          navigate(redirectTo, { replace: true });
        }
        // Otherwise the redirect is handled by the auth state change (PublicRoute).
      }
    } catch (err: unknown) {
      console.error('Error:', err);
      if (err instanceof Error) {
        setEmailError(err.message || 'An error occurred during authentication.');
      } else {
        setEmailError('An unexpected error occurred');
      }
    } finally {
      setIsEmailLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-3xl font-extrabold text-gray-900">
          My BHCS Portal
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 mb-8">
          {isSignUp ? 'Create your account' : 'Sign in to your account'}
        </p>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {showConfirmationBanner && (
          <div className="mb-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <LiveRegion>
                  <h3 className="text-sm font-medium text-blue-800">Email Confirmation Required</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>Please check your email for a confirmation link. You need to confirm your email address before you can sign in.</p>
                    <p className="mt-1">If you don't see the email, please check your spam folder.</p>
                  </div>
                </LiveRegion>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowConfirmationBanner(false)}
                    className="text-sm text-blue-600 hover:text-blue-500 focus:outline-none focus:underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {googleError && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {googleError}
            </div>
          )}
          
          {showResetForm ? (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 text-center pb-2 border-b border-gray-200">
                Reset Your Password
              </h3>
              
              {emailError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded text-sm">
                  {emailError}
                </div>
              )}
              
              {resetMessage && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded text-sm">
                  {resetMessage}
                </div>
              )}

              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="reset-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isResetLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {isResetLoading ? 'Sending...' : 'Send Reset Instructions'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetForm(false);
                    setEmailError(null);
                    setResetMessage(null);
                    setAuthStep('enterEmail');
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Google Sign In - Prioritized at the top */}
              {!isSignUp && authStep === 'enterEmail' && (
                <div>
                  <button
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading}
                    className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {!isGoogleLoading && (
                      <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                    )}
                    {isGoogleLoading ? 'Signing in...' : 'Continue with Google'}
                  </button>
                </div>
              )}

              {/* Separator */}
              {!isSignUp && authStep === 'enterEmail' && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">OR</span>
                  </div>
                </div>
              )}

              {/* Email Form */}
              <form onSubmit={handleEmailAuth} className="space-y-6">
                {emailError && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded text-sm">
                    {emailError}
                  </div>
                )}
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={authStep === 'enterPassword'}
                      className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${authStep === 'enterPassword' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>

                {authStep === 'enterPassword' && (
                  <>
                    <div>
                      <div className="flex items-center justify-between">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                          Password
                        </label>
                        {!isSignUp && (
                          <div className="text-sm">
                            <button
                              type="button"
                              onClick={() => {
                                setShowResetForm(true);
                                setEmailError(null);
                              }}
                              className="font-medium text-indigo-600 hover:text-indigo-500"
                            >
                              Forgot your password?
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="mt-1 relative">
                        <input
                          id="password"
                          name="password"
                          type={showPasswords ? "text" : "password"}
                          autoComplete={isSignUp ? "new-password" : "current-password"}
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                          aria-label={showPasswords ? "Hide password" : "Show password"}
                        >
                          {showPasswords ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {isSignUp && (
                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                          Confirm Password
                        </label>
                        <div className="mt-1 relative">
                          <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showPasswords ? "text" : "password"}
                            autoComplete="new-password"
                            required
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm pr-10"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div>
                  {authStep === 'enterEmail' ? (
                    <button
                      type="button"
                      onClick={handleContinueToPasswordStep}
                      disabled={isEmailLoading}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {isEmailLoading ? 'Processing...' : 'Continue'}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isEmailLoading}
                      className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isSignUp ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    >
                      {isEmailLoading ? 'Processing...' : (isSignUp ? 'Create Account' : 'Sign in')}
                    </button>
                  )}
                </div>
              </form>


              
              {authStep === 'enterEmail' && (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={handleToggleMode}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                    disabled={isEmailLoading || isGoogleLoading}
                  >
                    {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                  </button>
                </div>
              )}
              {authStep === 'enterPassword' && (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setAuthStep('enterEmail');
                      setPassword('');
                      setEmailError(null);
                    }}
                    className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center justify-center"
                  >
                    <span className="mr-1">❮</span> Go back
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500">
          By signing in, you agree to our{' '}
          <a href="/privacy-policy" className="text-gray-600 hover:text-gray-900 underline">Privacy Policy</a>
          {' '}and{' '}
          <a href="/terms-of-service" className="text-gray-600 hover:text-gray-900 underline">Terms of Service</a>
        </p>
      </div>
    </div>
  );
}
